import type { RefObject } from "react"
import { useCallback, useMemo } from "react"
import { useZeroVirtualizer } from "@rocicorp/zero-virtual/react"

import type {
	FeedbackWithRelations,
	InboxSelection,
} from "@productlane/zero/queries"
import type { CustomView } from "@productlane/zero/schema"
import { queries } from "@productlane/zero/queries"

import type { AssignedSubTab } from "./helpers/assigned-sub-tab"
import type { InboxState } from "@/lib/states/global-state"
import { useTimeTick } from "@/lib/hooks/use-time-tick"
import { useSortState } from "@/lib/states/global-state"
import { useAuthData } from "@/lib/zero"
import { mergeThreadFilters } from "../threads/filter/merge-thread-filters"
import { useThreadsFilterState } from "../threads/filter/threads-filter-state"
import { resolveSortColumn } from "../threads/hooks/use-threads-for-list"
import { useInboxAnchorReset } from "./inbox-anchor-reset"

const SLA_TICK_INTERVAL_MS = 5 * 60 * 1000

// Cursor shape that ZQL `start()` accepts on threadSearch — must include
// every field the active sort order might tie-break on, so any sort
// column the user picks works without extra plumbing.
interface ThreadCursor {
	createdAt: number
	lastStateChangeAt: number | null
	lastMessageAt: number
	painLevel: "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN"
	snoozedUntil: number | null
	id: string
}

const flipOrder = (order: "asc" | "desc"): "asc" | "desc" =>
	order === "asc" ? "desc" : "asc"

const toThreadCursor = (row: FeedbackWithRelations): ThreadCursor => ({
	createdAt: row.createdAt,
	lastStateChangeAt: row.lastStateChangeAt,
	lastMessageAt: row.lastMessageAt,
	painLevel: row.painLevel,
	snoozedUntil: row.snoozedUntil,
	id: row.id,
})

const getRowKey = (row: FeedbackWithRelations) => row.id

interface ListContext {
	tab: InboxState | "custom-view"
	sortColumn: string
	sortOrder: "asc" | "desc"
	stateScope?: AssignedSubTab
	// Stringified filters so structural changes invalidate persisted state.
	filtersHash: string
	// Bumped before a bulk mutation so the list re-anchors to the top instead
	// of keeping a backward anchor into rows the mutation deletes.
	anchorResetKey: number
}

interface UseInboxVirtualizerArgs {
	tab: InboxState
	overrideFilters?: CustomView["filters"]
	stateScope?: AssignedSubTab
	scrollElementRef: RefObject<HTMLElement | null>
	estimateSize: number
	permalinkID?: string | null
}

export const useInboxVirtualizer = ({
	tab,
	overrideFilters,
	stateScope,
	scrollElementRef,
	estimateSize,
	permalinkID,
}: UseInboxVirtualizerArgs) => {
	const decodedToken = useAuthData()
	const { sortByQueryColumn, sortByQueryColumnOrder } = useSortState()
	const liveFilters = useThreadsFilterState()
	const anchorResetKey = useInboxAnchorReset((s) => s.resetKey)

	const sortColumn = resolveSortColumn(tab, sortByQueryColumn)

	// Merge override filters (custom view) with live filters, mirroring the
	// behaviour of useThreadsForList so custom views still react to the
	// in-page filter controls.
	const filters = useMemo(
		() => mergeThreadFilters(overrideFilters, liveFilters),
		[overrideFilters, liveFilters],
	)

	const effectiveTab: InboxState | "custom-view" = overrideFilters
		? "custom-view"
		: tab

	// State scope narrows All / Snoozed / Done for custom views and for
	// built-in tabs that keep their identity filter (new, sla, etc.). Tabs
	// like my / open encode that state in the effective tab name instead.
	const effectiveStateScope = stateScope

	const tick = useTimeTick(SLA_TICK_INTERVAL_MS)
	const nowMs = useMemo(
		() =>
			effectiveTab === "sla-breached"
				? Math.floor(Date.now() / SLA_TICK_INTERVAL_MS) * SLA_TICK_INTERVAL_MS
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[effectiveTab, tick],
	)

	// Hash filters to a primitive string before depending on it. zero-virtual
	// compares listContextParams by reference (===); if we put the live
	// `filters` object in the dep array, any unrelated re-render where the
	// store hands us a fresh reference (even with identical contents) makes
	// listContextParams a new object, and zero-virtual reads that as "list
	// context changed" and resets scroll to the top — that was the random
	// shift on click.
	const filtersHash = useMemo(() => JSON.stringify(filters), [filters])

	// The same list, unpaged, for Cmd+A to fetch every matching id.
	const selectionArgs = useMemo<InboxSelection>(
		() => ({
			tab: effectiveTab,
			filters,
			stateScope: effectiveStateScope,
			nowMs,
		}),
		[effectiveTab, filters, effectiveStateScope, nowMs],
	)

	const listContextParams = useMemo<ListContext>(
		() => ({
			tab: effectiveTab,
			sortColumn,
			sortOrder: sortByQueryColumnOrder,
			stateScope: effectiveStateScope,
			filtersHash,
			anchorResetKey,
		}),
		[
			effectiveTab,
			sortColumn,
			sortByQueryColumnOrder,
			effectiveStateScope,
			filtersHash,
			anchorResetKey,
		],
	)

	const getPageQuery = useCallback(
		({
			limit,
			start,
			dir,
			settled,
		}: {
			limit: number
			// Typed as TStartRow but zero-virtual 0.5.0 passes the full TRow
			// here for forward/backward pagination (it only runs toStartRow
			// in permalink mode). Re-extract to a cursor before handing it
			// to ZQL — otherwise relations like `contact`/`company` and
			// undeclared columns like `showRecordCall` get fed into start()
			// and blow up NameMapper.columnName.
			start: (ThreadCursor & Partial<FeedbackWithRelations>) | null
			dir: "forward" | "backward"
			settled: boolean
		}) => ({
			query: queries.threadSearch({
				tab: effectiveTab,
				filters,
				stateScope: effectiveStateScope,
				sortColumn,
				sortOrder:
					dir === "forward"
						? sortByQueryColumnOrder
						: flipOrder(sortByQueryColumnOrder),
				limit,
				nowMs,
				startAfter: start
					? toThreadCursor(start as FeedbackWithRelations)
					: undefined,
			}),
			options: { ttl: settled ? "5m" : "10s" } as const,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			decodedToken,
			effectiveTab,
			filters,
			effectiveStateScope,
			sortColumn,
			sortByQueryColumnOrder,
			nowMs,
		],
	)

	const getSingleQuery = useCallback(
		({ id }: { id: string }) => ({
			query: queries.threadByID(id),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[decodedToken],
	)

	const getScrollElement = useCallback(
		() => scrollElementRef.current,
		[scrollElementRef],
	)

	const estimateSizeFn = useCallback(() => estimateSize, [estimateSize])

	// Deliberately not using useHistoryScrollState here. It reads
	// window.history.state via useSyncExternalStore, so every React Router
	// navigate() (which the inbox does on every thread click) returns a
	// different snapshot, which makes zero-virtual's layout effect think
	// scroll state changed and snap-restore to a stale offset — that was
	// the random "teleport" on click. React Router owns history, so we let
	// the DOM scroll position carry across navigations naturally.
	const result = useZeroVirtualizer({
		getScrollElement,
		estimateSize: estimateSizeFn,
		overscan: 15,
		listContextParams,
		permalinkID: permalinkID ?? undefined,
		getPageQuery,
		getSingleQuery,
		toStartRow: toThreadCursor,
		getRowKey,
	})

	return { ...result, selectionArgs }
}
